// Feeds visual — full-parity React port of insight-bi's insight-feed component.
// Each data row is a feed line split into a Left and a Right column, each with
// up to three stacked rows:
//   left  : First Row Left (14px) / Second Row Left (13px) / Third Row Left (12px grey)
//   right : First Row Right / Second Row Right / Third Row Right (right-aligned)
// When a Groups column is mapped, rows are bucketed by that value and shown
// under collapsible group headers (insight-bi uses Material expansion panels).
// Values are formatted with the shared number/date formatters; Icon FORMAT
// columns degrade to text (no CustomMenuImageServlet in twasta).

import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import type { NativeRendererProps, SelectHandler } from './nativeRenderers'
import { humanizeColumnLabel, mapVisualData, pickGroup, type MappedColumn, type MappedData } from './dataUtils'
import { formatCell } from './formatters'
import { displayPlainText, hasRichDisplay } from './displayCell'
import { useRowSelection, SELECT_BG, SELECT_RING_INSET } from './selection'
import { VIZ } from './surfaceTheme'

type RowPredicate = (row: Record<string, unknown>) => boolean

interface FeedLayout {
  groupCol?: MappedColumn
  firstLeft: MappedColumn[]
  firstRight: MappedColumn[]
  secondLeft: MappedColumn[]
  secondRight: MappedColumn[]
  thirdLeft: MappedColumn[]
  thirdRight: MappedColumn[]
  fallback: MappedColumn[]
}

function buildLayout(md: MappedData): FeedLayout {
  const vis = (cols: MappedColumn[]) => cols.filter((c) => !c.hidden)
  const l: FeedLayout = {
    groupCol: vis(pickGroup(md, 'groups'))[0],
    firstLeft: vis(pickGroup(md, 'firstrowleft')),
    firstRight: vis(pickGroup(md, 'firstrowright')),
    secondLeft: vis(pickGroup(md, 'secondrowleft')),
    secondRight: vis(pickGroup(md, 'secondrowright')),
    thirdLeft: vis(pickGroup(md, 'thirdrowleft')),
    thirdRight: vis(pickGroup(md, 'thirdrowright')),
    fallback: [],
  }
  const hasLayout = [l.firstLeft, l.firstRight, l.secondLeft, l.secondRight, l.thirdLeft, l.thirdRight]
    .some((a) => a.length)
  if (!hasLayout) {
    let cols = vis([...pickGroup(md, 'rows'), ...pickGroup(md, 'values'), ...pickGroup(md, 'columns')])
    if (!cols.length) {
      cols = Object.keys(md.schema)
        .filter((k) => k !== 'id')
        .map((k) => ({ name: k, label: humanizeColumnLabel(k), type: md.schema[k]?.type }))
    }
    l.fallback = cols
  }
  return l
}

// Feed lines join several column values into one text row, so honour the
// display format as PLAIN TEXT (number / date / status label / template …),
// falling back to the legacy mask formatter.
const fmt = (row: Record<string, unknown>, c: MappedColumn) =>
  hasRichDisplay(c)
    ? displayPlainText(row[c.name], c, row)
    : formatCell(row[c.name], { type: c.type, coltype: c.coltype, format: c.format, pattern: c.pattern })

// One side (left / right) of a feed line: up to three stacked rows.
function FeedSide({ row, rows, align }: { row: Record<string, unknown>; rows: MappedColumn[][]; align: 'left' | 'right' }) {
  const sizes = [14, 13, 12]
  const colours = ['var(--pg-text, var(--color-text, #222))', 'var(--pg-text, var(--color-text, #333))', VIZ.faint]
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: align === 'right' ? 'flex-end' : 'flex-start', minWidth: 0, flex: 1 }}>
      {rows.map((cols, i) =>
        cols.length ? (
          <div key={i} style={{ fontSize: sizes[i], color: colours[i], textAlign: align, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {cols.map((c) => fmt(row, c)).filter((v) => v !== '').join('  ')}
          </div>
        ) : null,
      )}
    </div>
  )
}

function FeedItem({ row, layout, onSelect, selected }: { row: Record<string, unknown>; layout: FeedLayout; onSelect?: SelectHandler; selected?: boolean }) {
  const [hover, setHover] = useState(false)
  const clickProps = onSelect
    ? { onClick: () => onSelect(row), role: 'button' as const, tabIndex: 0, 'aria-pressed': !!selected }
    : {}
  const cursor = onSelect ? 'pointer' : undefined
  const bg = selected ? SELECT_BG : hover ? VIZ.hover : 'transparent'
  const selRing = selected ? { boxShadow: SELECT_RING_INSET } : null
  if (layout.fallback.length) {
    return (
      <div
        {...clickProps}
        style={{ ...feedRowStyle, ...selRing, background: bg, display: 'flex', flexWrap: 'wrap', gap: 12, cursor }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {layout.fallback.map((c, i) => (
          <span key={c.name} style={{ fontSize: 13 }}>
            {i === 0
              ? <strong>{fmt(row, c)}</strong>
              : <><span style={{ opacity: 0.6 }}>{c.label}:</span> {fmt(row, c)}</>}
          </span>
        ))}
      </div>
    )
  }
  return (
    <div
      {...clickProps}
      style={{ ...feedRowStyle, ...selRing, background: bg, display: 'flex', gap: 12, cursor }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <FeedSide row={row} align="left" rows={[layout.firstLeft, layout.secondLeft, layout.thirdLeft]} />
      <FeedSide row={row} align="right" rows={[layout.firstRight, layout.secondRight, layout.thirdRight]} />
    </div>
  )
}

function FeedGroup({ name, rows, layout, onSelect, isSelected }: { name: string; rows: Array<Record<string, unknown>>; layout: FeedLayout; onSelect?: SelectHandler; isSelected?: RowPredicate }) {
  const [open, setOpen] = useState(true)
  return (
    <div style={{ borderBottom: '1px solid var(--pg-border, var(--color-border, #eee))' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer', background: 'var(--pg-surface, var(--color-surface-2, #f8f9fa))', fontWeight: 600, fontSize: 13, color: 'var(--pg-heading, var(--color-text, #111))', userSelect: 'none' }}
      >
        <span style={{ width: 12, opacity: 0.6 }}>{open ? '▾' : '▸'}</span>
        <span>{name}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--pg-muted, var(--color-text-secondary, #6b7280))', fontWeight: 400 }}>{rows.length}</span>
      </div>
      {open && rows.map((r, i) => <FeedItem key={i} row={r} layout={layout} onSelect={onSelect} selected={isSelected?.(r)} />)}
    </div>
  )
}

export function FeedsVisual({ jsonData, visualLayout, onSelect }: NativeRendererProps) {
  const { select, isSelected } = useRowSelection(onSelect)
  const md = useMemo(() => mapVisualData(jsonData, visualLayout), [jsonData, visualLayout])
  const layout = useMemo(() => buildLayout(md), [md])

  if (!md.rows.length) {
    return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-muted, var(--color-text-secondary, #888))', textAlign: 'center' }}>No items.</div>
  }

  // Grouped mode — bucket rows by the Groups column value.
  if (layout.groupCol) {
    const gc = layout.groupCol
    const buckets = new Map<string, Array<Record<string, unknown>>>()
    for (const r of md.rows) {
      const raw = r[gc.name]
      const key = raw == null || raw === '' ? 'Not Found' : fmt(r, gc)
      if (!buckets.has(key)) buckets.set(key, [])
      buckets.get(key)!.push(r)
    }
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
        {[...buckets.entries()].map(([name, rows]) => (
          <FeedGroup key={name} name={name} rows={rows} layout={layout} onSelect={select} isSelected={isSelected} />
        ))}
      </div>
    )
  }

  // Flat mode.
  return (
    <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
      {md.rows.map((r, i) => <FeedItem key={i} row={r} layout={layout} onSelect={select} selected={isSelected(r)} />)}
    </div>
  )
}

const feedRowStyle: CSSProperties = {
  padding: '10px 12px',
  boxShadow: 'inset 0 -1px 0 0 var(--pg-border, var(--color-border, rgba(0,0,0,0.10)))',
  transition: 'background 0.15s',
  alignItems: 'flex-start',
}

export default FeedsVisual
